import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Check, Lock, CircleDashed, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";
import { PageHeader } from "@/pages/Dashboard";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const CAT_ALL = "All";

function ComplianceItem({ item, onToggle }) {
  const locked = item.derived; // auto-satisfied from another module — can't be unchecked here
  return (
    <div className="rounded-xl border border-white/8 bg-[#0f0f11] p-4 flex items-start gap-4" data-testid={`compliance-item-${item.id}`}>
      <button
        onClick={() => !locked && onToggle(item)}
        disabled={locked}
        data-testid={`compliance-toggle-${item.id}`}
        className={`mt-0.5 w-6 h-6 rounded-md shrink-0 flex items-center justify-center border transition-colors ${
          item.completed
            ? "fv-gold-gradient border-transparent"
            : "border-white/20 hover:border-[#D4AF37]/60"
        } ${locked ? "cursor-not-allowed opacity-90" : "cursor-pointer"}`}
        aria-label={item.completed ? "Completed" : "Mark complete"}
      >
        {item.completed ? <Check className="w-4 h-4 text-[#0A0A0B]" /> : <CircleDashed className="w-4 h-4 text-[#52525B]" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-medium ${item.completed ? "text-white" : "text-[#D4D4D8]"}`}>{item.title}</span>
          <span className="text-[9px] uppercase tracking-wider text-[#71717A] border border-white/10 rounded-full px-2 py-0.5">{item.category}</span>
          <span className="text-[9px] uppercase tracking-wider text-[#71717A]">{item.frequency}</span>
          {locked && (
            <span className="text-[9px] uppercase tracking-wider text-emerald-400 flex items-center gap-1" data-testid={`compliance-auto-${item.id}`}>
              <Lock className="w-3 h-3" /> Auto-verified
            </span>
          )}
        </div>
        <p className="text-sm text-[#71717A] mt-1">{item.description}</p>
        <p className="text-xs text-[#D4AF37]/90 mt-1.5">{item.why}</p>
      </div>
    </div>
  );
}

export default function Compliance() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState(CAT_ALL);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/compliance/overview");
      setData(res.data);
    } catch (err) { logError("Compliance", "Load failed", err?.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    try {
      const res = await api.patch(`/compliance/items/${item.id}`, { completed: !item.completed });
      setData(res.data);
      toast.success(item.completed ? "Marked incomplete" : "Marked complete");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const stats = data?.stats || { done: 0, total: 0, pct: 0 };
  const categories = [CAT_ALL, ...(data?.categories || [])];
  const items = (data?.items || []).filter((i) => cat === CAT_ALL || i.category === cat);

  return (
    <div className="fv-fade-up">
      <PageHeader eyebrow="Stay in good standing" title="Compliance Center"
        subtitle="Track the legal and regulatory obligations that keep your business protected — the AI Advisor uses this to guide you." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <div className="lg:col-span-2 rounded-2xl border border-white/8 bg-[#121214] p-7">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-[#71717A]">
              <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-[11px] uppercase tracking-wider">Compliance readiness</span>
            </div>
            <span className="text-sm text-[#71717A]" data-testid="compliance-count">{stats.done}/{stats.total} complete</span>
          </div>
          <div className="font-heading text-5xl text-white mb-4" data-testid="compliance-pct">{stats.pct}%</div>
          <Progress value={stats.pct} className="h-2.5 bg-white/5" />
        </div>

        <div onClick={() => navigate("/ai-assistant")} data-testid="compliance-ai-cta"
          className="fv-card-hover cursor-pointer rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-7 flex flex-col justify-between">
          <div>
            <div className="w-11 h-11 rounded-xl fv-gold-gradient flex items-center justify-center mb-3">
              <Sparkles className="w-5 h-5 text-[#0A0A0B]" />
            </div>
            <h3 className="font-heading text-xl text-white mb-1.5">Ask about compliance</h3>
            <p className="text-[#A1A1AA] text-sm">Your AI Advisor knows exactly which obligations you've met and what's next.</p>
          </div>
          <div className="mt-5 text-[#D4AF37] text-sm">Open AI Advisor →</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="compliance-categories">
        {categories.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`compliance-cat-${c}`}
            className={`text-xs rounded-full px-4 py-1.5 border transition-colors ${
              cat === c ? "fv-gold-gradient text-[#0A0A0B] border-transparent font-medium"
                        : "border-white/12 text-[#A1A1AA] hover:border-[#D4AF37]/40"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="compliance-list">
        {items.map((item) => <ComplianceItem key={item.id} item={item} onToggle={toggle} />)}
      </div>
    </div>
  );
}
